import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Text, AnnouncementModalLayout } from '@wix/design-system';
import { PackagePickerType } from '../../../enums';
import { RootStore, withInjectedRootStore } from '../../../providers';
import MainIllustration from '../../../assets/images/no-sites-modal/noSitesImage.svg?url';
import StudioIllustration from '../../../assets/images/no-sites-modal/studioNoSitesImage.png';
import EditorXIllustration from '../../../assets/images/editorx-bg/no_sites_desktop.png';
import s from './NewNoWebsiteModal.scss';

interface NoWebsiteModalProps {
  isOpen: boolean;
  onClose(): void;
  rootStore: RootStore;
}

interface ModalContent {
  title: string;
  subtitle: string;
  actionUrl: string;
  illustration: string;
}

export interface ModalContentProps {
  onClose(): void;
  onConfirm(): void;
  content: ModalContent;
}

const modalContentMap: Partial<Record<PackagePickerType, ModalContent>> = {
  [PackagePickerType.Default]: {
    title: 'no_sites_modal.title',
    subtitle: 'no_sites_modal.subtitle',
    actionUrl: 'https://www.wix.com/website/templates',
    illustration: MainIllustration,
  },
  [PackagePickerType.Sunrise]: {
    title: 'no_sites_modal.title',
    subtitle: 'no_sites_modal.subtitle',
    actionUrl: 'https://www.wix.com/website/templates',
    illustration: MainIllustration,
  },
  [PackagePickerType.EditorX]: {
    title: 'no_sites_modal.title',
    subtitle: 'no_sites_modal.subtitle',
    actionUrl: 'https://www.editorx.com/templates',
    illustration: EditorXIllustration,
  },
  [PackagePickerType.Dayful]: {
    title: 'dayful.no_sites_modal.title',
    subtitle: 'dayful.no_sites_modal.subtitle',
    actionUrl: 'https://www.wix.com/intro/dayful',
    illustration: MainIllustration,
  },
  [PackagePickerType.Studio]: {
    title: 'studio.no_sites_modal.title',
    subtitle: 'studio.no_sites_modal.subtitle',
    actionUrl: 'https://www.wix.com/studio/templates',
    illustration: StudioIllustration,
  },
  [PackagePickerType.EditorXToStudio]: {
    title: 'studio.no_sites_modal.title',
    subtitle: 'studio.no_sites_modal.subtitle',
    actionUrl: 'https://www.wix.com/studio/templates',
    illustration: StudioIllustration,
  },
  [PackagePickerType.Sandbox]: {
    title: 'no_sites_modal.title',
    subtitle: 'no_sites_modal.subtitle',
    actionUrl: '/website/templates',
    illustration: MainIllustration,
  },
};

const NoWebsiteModalComp: React.FC<NoWebsiteModalProps> = ({
  isOpen,
  onClose,
  rootStore,
}) => {
  const { packagePickerType } = rootStore.appStore.appContext;
  const modalContent =
    modalContentMap[packagePickerType] ??
    modalContentMap[PackagePickerType.Default]!;
  const isStudio = packagePickerType === PackagePickerType.Studio;

  const handleConfirmation = () =>
    window.location.assign(modalContent.actionUrl);

  return (
    <Modal dataHook='no-website-modal' isOpen={isOpen}>
      <ModalContent
        onClose={onClose}
        onConfirm={handleConfirmation}
        content={modalContent}
        buttonClassName={isStudio ? s.studioModalButton : ''}
      />
    </Modal>
  );
};

export const ModalContent: React.FC<
  ModalContentProps & { buttonClassName: string }
> = ({ onClose, onConfirm, content, buttonClassName }) => {
  const { t } = useTranslation();
  const { title, subtitle, illustration } = content;

  return (
    <AnnouncementModalLayout
      illustration={illustration}
      title={t(title)}
      onCloseButtonClick={onClose}
      primaryButtonText={t('no_sites_modal.cta')}
      primaryButtonOnClick={onConfirm}
      primaryButtonProps={{ className: buttonClassName }}
    >
      <Text>{t(subtitle)}</Text>
    </AnnouncementModalLayout>
  );
};

export const NewNoWebsiteModal = withInjectedRootStore(NoWebsiteModalComp);
